import { clearLoginSession } from '../api/auth.js';

/**
 * IM 引擎单例：统一持有 WebSocket、离线消息拉取与实时消息处理。
 * Home / ChatDialog 等 UI 宿主通过 registerHost 注册 $refs，引擎负责 IM 核心逻辑。
 */
class ImEngine {
	constructor() {
		this.vm = null;
		this.initialized = false;
		this.starting = null;
		this.wsHandlersRegistered = false;
		this.eventBusRegistered = false;
		this.reconnecting = false;
		this.hosts = new Map();
		this.privateMessagesBuffer = [];
		this.groupMessagesBuffer = [];
		this.systemMessagesBuffer = [];
		this.lastPlayAudioTime = Date.now() - 1000;
		this.audio = null;
		this.notification = null;
	}

	registerHost(vm, options = {}) {
		this.hosts.set(vm._uid, {
			vm,
			getRefs: () => vm.$refs,
			shouldSuppressNotification: options.shouldSuppressNotification,
		});
		if (this.vm == null) {
			this.vm = vm;
		}
	}

	unregisterHost(vm) {
		this.hosts.delete(vm._uid);
	}

	getVm() {
		return this.vm;
	}

	getRef(name) {
		for (const host of this.hosts.values()) {
			const ref = host.getRefs?.()?.[name];
			if (ref) {
				return ref;
			}
		}
		return null;
	}

	shouldSuppressNotification() {
		for (const host of this.hosts.values()) {
			if (host.shouldSuppressNotification?.()) {
				return true;
			}
		}
		return false;
	}

	async ensureStarted(vm) {
		this.registerHost(vm);
		if (this.initialized) {
			return;
		}
		if (this.starting) {
			return this.starting;
		}
		this.starting = this._start(vm).finally(() => {
			this.starting = null;
		});
		return this.starting;
	}

	async _start(vm) {
		this.vm = vm;
		vm.$wsApi.close();
		this._setupEventBus(vm);
		try {
			await this.loadStore(vm);
			this._registerWsHandlers(vm);
			vm.$wsApi.connect(process.env.VUE_APP_WS_URL, sessionStorage.getItem('accessToken'));
			this.initialized = true;
		} catch (e) {
			console.log(e);
			vm.$message.error(vm.$t('home.initFailed'));
			this.onExit(vm);
			throw e;
		}
	}

	_setupEventBus(vm) {
		if (this.eventBusRegistered) {
			return;
		}
		this.eventBusRegistered = true;
		vm.$eventBus.$on('exit', () => this.onExit(vm));
		vm.$eventBus.$on('openPrivateVideo', (rctInfo) => {
			vm.$nextTick(() => this.getRef('rtcPrivateVideo')?.open(rctInfo));
		});
		vm.$eventBus.$on('openGroupVideo', (rctInfo) => {
			vm.$nextTick(() => this.getRef('rtcGroupVideo')?.open(rctInfo));
		});
		vm.$eventBus.$on('openUserInfo', (user, pos) => {
			vm.$nextTick(() => this.getRef('userInfo')?.open(user, pos));
		});
		vm.$eventBus.$on('openGroupInfo', (group, token, pos) => {
			vm.$nextTick(() => this.getRef('groupInfo')?.open(group, token, pos));
		});
		vm.$eventBus.$on('openFullImage', (url) => {
			vm.$nextTick(() => this.getRef('fullImage')?.open(url));
		});
		vm.$eventBus.$on('openPreviewFile', (url) => {
			vm.$nextTick(() => this.getRef('previewFile')?.open(url));
		});
	}

	_registerWsHandlers(vm) {
		if (this.wsHandlersRegistered) {
			return;
		}
		this.wsHandlersRegistered = true;
		vm.$wsApi.onConnect(() => {
			if (this.reconnecting) {
				this.onReconnectWs(vm);
			} else {
				this.pullOfflineMessage(vm);
			}
		});
		vm.$wsApi.onMessage((cmd, message) => {
			if (cmd == 2) {
				vm.$wsApi.close(3000);
				vm.$alert(vm.$t('home.forceLogout'), vm.$t('home.forceLogoutTitle'), {
					confirmButtonText: vm.$t('common.ok'),
					callback: () => this.onExit(vm),
				});
			} else if (cmd == 3) {
				if (vm.chatStore.loading) {
					this.privateMessagesBuffer.push(message);
				} else {
					this.handlePrivateMessage(vm, message);
				}
			} else if (cmd == 4) {
				if (vm.chatStore.loading) {
					this.groupMessagesBuffer.push(message);
				} else {
					this.handleGroupMessage(vm, message);
				}
			} else if (cmd == 5) {
				if (vm.chatStore.loading) {
					this.systemMessagesBuffer.push(message);
				} else {
					this.handleSystemMessage(vm, message);
				}
			}
		});
		vm.$wsApi.onClose((e) => {
			console.log(e);
			if (e.code != 3000) {
				this.reconnectWs(vm);
			}
		});
	}

	reconnectWs(vm) {
		this.reconnecting = true;
		vm.userStore.loadUser().then(() => {
			vm.$message.error(vm.$t('home.reconnecting'));
			vm.$wsApi.reconnect(process.env.VUE_APP_WS_URL, sessionStorage.getItem('accessToken'));
		}).catch(() => {
			setTimeout(() => this.reconnectWs(vm), 10000);
		});
	}

	onReconnectWs(vm) {
		this.reconnecting = false;
		const promises = [];
		promises.push(vm.friendStore.pullFriends());
		promises.push(vm.groupStore.pullGroups());
		Promise.all(promises).then(() => {
			this.pullOfflineMessage(vm);
			vm.friendStore.refreshOnline();
			vm.friendStore.loadRequest();
			vm.$message.success(vm.$t('home.reconnectSuccess'));
		}).catch(() => {
			vm.$message.error(vm.$t('home.initFailed'));
			this.onExit(vm);
		});
	}

	async loadStore(vm) {
		await vm.userStore.loadUser();
		await vm.$db.open(vm.userStore.userInfo.id);
		await vm.friendStore.loadFriend();
		return Promise.all([
			vm.groupStore.loadGroup(),
			vm.configStore.loadConfig(),
			vm.chatStore.loadConversations(),
		]);
	}

	unloadStore(vm) {
		vm.friendStore.clear();
		vm.groupStore.clear();
		vm.chatStore.clear();
		vm.userStore.clear();
	}

	pullOfflineMessage(vm) {
		const timeStamp = Date.now();
		vm.chatStore.setLoading(true);
		const maxPrivateMessageId = vm.chatStore.findMaxMessageId(vm.$enums.CONVERSATION_TYPE.PRIVATE);
		const maxGroupMessageId = vm.chatStore.findMaxMessageId(vm.$enums.CONVERSATION_TYPE.GROUP);
		const maxSeqNo = vm.chatStore.findMaxSeqNo(vm.$enums.CONVERSATION_TYPE.SYSTEM);
		Promise.all([
			this.pullPrivateOfflineMessage(vm, maxPrivateMessageId),
			this.pullGroupOfflineMessage(vm, maxGroupMessageId),
			this.pullSystemOfflineMessage(vm, maxSeqNo),
		]).then(async (messages) => {
			await this.handlePrivateOfflineMessage(vm, messages[0]);
			await this.handleGroupOfflineMessage(vm, messages[1]);
			await this.handleSystemOfflineMessage(vm, messages[2]);
			for (const m of this.privateMessagesBuffer) {
				await this.handlePrivateMessage(vm, m);
			}
			for (const m of this.groupMessagesBuffer) {
				await this.handleGroupMessage(vm, m);
			}
			for (const m of this.systemMessagesBuffer) {
				await this.handleSystemMessage(vm, m);
			}
			this.privateMessagesBuffer = [];
			this.groupMessagesBuffer = [];
			this.systemMessagesBuffer = [];
			vm.chatStore.setLoading(false);
			const size = messages[0].length + messages[1].length + messages[2].length;
			console.log('加载离线消息耗时:', Date.now() - timeStamp, ',消息数量:', size);
		}).catch((e) => {
			console.log(e);
			vm.$message.error(vm.$t('home.pullMsgFailed'));
			this.onExit(vm);
		});
	}

	async handlePrivateOfflineMessage(vm, messages) {
		if (!messages || !messages.length) {
			return;
		}
		const conversationMap = new Map();
		const messageMap = new Map(messages.map(m => [m.id, m]));
		const tmpMessages = [];
		const mine = vm.userStore.userInfo;
		for (const m of messages) {
			m.selfSend = m.sendId == mine.id;
			const friendId = m.selfSend ? m.recvId : m.sendId;
			const convKey = vm.$db.buildConversationKey(vm.$enums.CONVERSATION_TYPE.PRIVATE, friendId);
			m.convKey = convKey;
			let conversation = conversationMap.get(friendId);
			if (!conversation) {
				conversation = await vm.$db.findConversationByKey(convKey);
				if (!conversation) {
					const friend = this.loadFriendInfo(vm, friendId);
					conversation = {
						key: convKey,
						type: vm.$enums.CONVERSATION_TYPE.PRIVATE,
						targetId: friend.id,
						showName: friend.showNickName,
						headImage: friend.headImage,
						companyName: friend.companyName,
						isDnd: friend.isDnd,
						isTop: friend.isTop,
						lastContent: '',
						lastSendTime: Date.now(),
						optTime: Date.now(),
						unreadCount: 0,
						lastTimeTip: 0,
						maxMessageId: 0,
						minSeqNo: 0,
						maxSeqNo: 0,
						maxReadedId: 0,
					};
				}
				conversationMap.set(friendId, conversation);
			}
			conversation.lastSendTime = m.sendTime;
			conversation.optTime = m.sendTime;
			conversation.maxMessageId = Math.max(conversation.maxMessageId, m.id);
			conversation.maxSeqNo = Math.max(conversation.maxSeqNo, m.seqNo);
			if (m.selfSend || m.status == vm.$enums.MESSAGE_STATUS.READED) {
				conversation.unreadCount = 0;
			} else if (m.status != vm.$enums.MESSAGE_STATUS.RECALL && m.type != vm.$enums.MESSAGE_TYPE.TIP_TEXT) {
				conversation.unreadCount++;
			}
			if (m.type == vm.$enums.MESSAGE_TYPE.RECALL) {
				const recallMessageId = Number(JSON.parse(m.content).id);
				const recallMessageTip = JSON.parse(m.content).tip || '';
				let recallMessage = messageMap.get(recallMessageId);
				if (!recallMessage) {
					recallMessage = await vm.$db.findMessageById(recallMessageId, convKey);
					if (!recallMessage) {
						continue;
					}
					tmpMessages.push(recallMessage);
				}
				recallMessage.status = vm.$enums.MESSAGE_STATUS.PENDING;
				recallMessage.content = recallMessageTip;
				recallMessage.type = vm.$enums.MESSAGE_TYPE.TIP_TEXT;
				conversation.lastContent = vm.$msgUtil.previewContent(recallMessage);
				conversation.sendNickName = '';
				if (!messageMap.has(recallMessageId)) {
					const quoteMessages = await vm.$db.findQuoteMessages(recallMessage);
					quoteMessages.forEach((m1) => {
						m1.quoteMessage.content = JSON.stringify({ key: 'tip.recall.quote' });
						m1.quoteMessage.status = vm.$enums.MESSAGE_STATUS.RECALL;
						m1.quoteMessage.type = vm.$enums.MESSAGE_TYPE.TIP_TEXT;
					});
					tmpMessages.push(...quoteMessages);
				}
			} else if (m.status != vm.$enums.MESSAGE_STATUS.RECALL) {
				conversation.lastContent = vm.$msgUtil.previewContent(m);
			}
		}
		const conversations = Array.from(conversationMap.values());
		await vm.$db.saveConversationAndMessage(conversations, messages.concat(tmpMessages));
		vm.chatStore.append(conversations);
	}

	async handleGroupOfflineMessage(vm, messages) {
		if (!messages || !messages.length) {
			return;
		}
		const conversationMap = new Map();
		const messageMap = new Map(messages.map(m => [m.id, m]));
		const tmpMessages = [];
		const mine = vm.userStore.userInfo;
		for (const m of messages) {
			m.selfSend = m.sendId == mine.id;
			const groupId = m.groupId;
			const convKey = vm.$db.buildConversationKey(vm.$enums.CONVERSATION_TYPE.GROUP, groupId);
			m.convKey = convKey;
			let conversation = conversationMap.get(groupId);
			if (!conversation) {
				conversation = await vm.$db.findConversationByKey(convKey);
				if (!conversation) {
					const group = this.loadGroupInfo(vm, groupId);
					conversation = {
						key: convKey,
						type: vm.$enums.CONVERSATION_TYPE.GROUP,
						targetId: group.id,
						showName: group.showGroupName,
						headImage: group.headImageThumb,
						isDnd: group.isDnd,
						isTop: group.isTop,
						lastContent: '',
						lastSendTime: Date.now(),
						optTime: Date.now(),
						unreadCount: 0,
						atMe: false,
						atAll: false,
						lastAtMessageId: -1,
						lastTimeTip: 0,
						maxMessageId: 0,
						minSeqNo: 0,
						maxSeqNo: 0,
						maxReadedId: 0,
					};
				}
				conversationMap.set(groupId, conversation);
			}
			conversation.lastSendTime = m.sendTime;
			conversation.optTime = m.sendTime;
			conversation.maxMessageId = Math.max(conversation.maxMessageId, m.id);
			conversation.maxSeqNo = Math.max(conversation.maxSeqNo, m.seqNo);
			if (m.selfSend || m.status == vm.$enums.MESSAGE_STATUS.READED) {
				conversation.unreadCount = 0;
			} else if (m.status != vm.$enums.MESSAGE_STATUS.RECALL && m.type != vm.$enums.MESSAGE_TYPE.TIP_TEXT) {
				conversation.unreadCount++;
			}
			if (!m.selfSend && m.atUserIds && m.status != vm.$enums.MESSAGE_STATUS.READED) {
				if (m.atUserIds.indexOf(mine.id) >= 0) {
					conversation.atMe = true;
					conversation.lastAtMessageId = m.id;
				}
				if (m.atUserIds.indexOf(-1) >= 0) {
					conversation.atAll = true;
					conversation.lastAtMessageId = m.id;
				}
			}
			if (m.type == vm.$enums.MESSAGE_TYPE.RECALL) {
				const recallMessageId = Number(JSON.parse(m.content).id);
				const recallMessageTip = JSON.parse(m.content).tip || '';
				let recallMessage = messageMap.get(recallMessageId);
				if (!recallMessage) {
					recallMessage = await vm.$db.findMessageById(recallMessageId, convKey);
					if (!recallMessage) {
						continue;
					}
					tmpMessages.push(recallMessage);
				}
				recallMessage.status = vm.$enums.MESSAGE_STATUS.PENDING;
				recallMessage.content = recallMessageTip;
				recallMessage.type = vm.$enums.MESSAGE_TYPE.TIP_TEXT;
				conversation.lastContent = vm.$msgUtil.previewContent(recallMessage);
				conversation.sendNickName = '';
				if (!messageMap.has(recallMessageId)) {
					const quoteMessages = await vm.$db.findQuoteMessages(recallMessage);
					quoteMessages.forEach((m1) => {
						m1.quoteMessage.content = JSON.stringify({ key: 'tip.recall.quote' });
						m1.quoteMessage.status = vm.$enums.MESSAGE_STATUS.RECALL;
						m1.quoteMessage.type = vm.$enums.MESSAGE_TYPE.TIP_TEXT;
					});
					tmpMessages.push(...quoteMessages);
				}
			} else if (m.status != vm.$enums.MESSAGE_STATUS.RECALL) {
				conversation.lastContent = vm.$msgUtil.previewContent(m);
				conversation.sendNickName = m.selfSend ? '' : m.sendNickName;
			}
		}
		const conversations = Array.from(conversationMap.values());
		await vm.$db.saveConversationAndMessage(conversations, messages.concat(tmpMessages));
		vm.chatStore.append(conversations);
	}

	async handleSystemOfflineMessage(vm, messages) {
		if (!messages || !messages.length) {
			return;
		}
		const convKey = vm.$db.buildConversationKey(vm.$enums.CONVERSATION_TYPE.SYSTEM, 0);
		let conversation = await vm.$db.findConversationByKey(convKey);
		if (!conversation) {
			const headImage = require('@/assets/image/chat_system.png');
			conversation = {
				key: convKey,
				type: vm.$enums.CONVERSATION_TYPE.SYSTEM,
				targetId: 0,
				showName: vm.$t('home.systemNotify'),
				headImage: headImage,
				isDnd: false,
				isTop: false,
				lastContent: '',
				lastSendTime: Date.now(),
				optTime: Date.now(),
				unreadCount: 0,
				lastTimeTip: 0,
				maxMessageId: 0,
				minSeqNo: 0,
				maxSeqNo: 0,
				maxReadedId: 0,
			};
		}
		for (const m of messages) {
			m.localId = vm.$nextSnowflakeId();
			m.convKey = convKey;
			conversation.lastContent = vm.$msgUtil.previewContent(m);
			conversation.lastSendTime = m.sendTime;
			conversation.optTime = m.sendTime;
			conversation.maxMessageId = Math.max(conversation.maxMessageId, m.id);
			conversation.maxSeqNo = Math.max(conversation.maxSeqNo, m.seqNo);
			if (m.status != vm.$enums.MESSAGE_STATUS.READED) {
				conversation.unreadCount++;
			}
		}
		await vm.$db.saveConversationAndMessage([conversation], messages);
		vm.chatStore.append([conversation]);
	}

	pullPrivateOfflineMessage(vm, minId) {
		return vm.$http({
			url: '/message/private/loadOfflineMessage?minId=' + minId,
			method: 'GET',
		});
	}

	pullGroupOfflineMessage(vm, minId) {
		return vm.$http({
			url: '/message/group/loadOfflineMessage?minId=' + minId,
			method: 'GET',
		});
	}

	pullSystemOfflineMessage(vm, minSeqNo) {
		return vm.$http({
			url: '/message/system/loadOfflineMessage?minSeqNo=' + minSeqNo,
			method: 'GET',
		});
	}

	async handlePrivateMessage(vm, m) {
		const mine = vm.userStore.userInfo;
		m.selfSend = m.sendId == mine.id;
		const friendId = m.selfSend ? m.recvId : m.sendId;
		const convKey = vm.$db.buildConversationKey(vm.$enums.CONVERSATION_TYPE.PRIVATE, friendId);
		if (m.type == vm.$enums.MESSAGE_TYPE.READED) {
			await vm.chatStore.resetUnreadCount(convKey);
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.RECEIPT) {
			await vm.chatStore.readedMessage(convKey);
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.RECALL) {
			await vm.chatStore.recallMessage(convKey, m);
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.FRIEND_REQ_APPLY) {
			vm.friendStore.addRequest(JSON.parse(m.content));
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.FRIEND_REQ_APPROVE
			|| m.type == vm.$enums.MESSAGE_TYPE.FRIEND_REQ_REJECT
			|| m.type == vm.$enums.MESSAGE_TYPE.FRIEND_REQ_RECALL) {
			vm.friendStore.removeRequest(JSON.parse(m.content).id);
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.FRIEND_NEW) {
			vm.friendStore.addFriend(JSON.parse(m.content));
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.FRIEND_DEL) {
			vm.friendStore.removeFriend(friendId);
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.FRIEND_ONLINE) {
			vm.friendStore.updateOnlineStatus(JSON.parse(m.content));
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.FRIEND_DND) {
			vm.friendStore.setDnd(friendId, JSON.parse(m.content));
			await vm.chatStore.setDnd(convKey, JSON.parse(m.content));
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.FRIEND_TOP) {
			vm.friendStore.setTop(friendId, JSON.parse(m.content));
			await vm.chatStore.setTop(convKey, JSON.parse(m.content));
			return;
		}
		if (vm.$msgType.isRtcPrivate(m.type)) {
			this.getRef('rtcPrivateVideo')?.onRTCMessage(m);
			return;
		}
		if (vm.$msgType.isNormal(m.type) || vm.$msgType.isTip(m.type) || vm.$msgType.isAction(m.type)) {
			const friend = this.loadFriendInfo(vm, friendId);
			await this.insertPrivateMessage(vm, friend, m);
		}
	}

	async insertPrivateMessage(vm, friend, m) {
		const convKey = vm.$db.buildConversationKey(vm.$enums.CONVERSATION_TYPE.PRIVATE, friend.id);
		const chatInfo = {
			type: vm.$enums.CONVERSATION_TYPE.PRIVATE,
			targetId: friend.id,
			showName: friend.showNickName,
			headImage: friend.headImage,
			companyName: friend.companyName,
			isDnd: friend.isDnd,
			isTop: friend.isTop,
		};
		await vm.chatStore.openChat(chatInfo);
		await vm.chatStore.insertMessage(convKey, m);
		if (vm.chatStore.isActive(convKey)) {
			vm.$eventBus.$emit('newMessage', m);
		}
		if (!friend.isDnd && !m.selfSend && vm.$msgType.isNormal(m.type)
			&& m.status != vm.$enums.MESSAGE_STATUS.READED) {
			this.playAudioTip(vm);
			const conv = vm.chatStore.conversationMap.get(convKey);
			this.showNotification(vm, conv, friend.showNickName, friend.headImage);
		}
	}

	async handleGroupMessage(vm, m) {
		const mine = vm.userStore.userInfo;
		m.selfSend = m.sendId == mine.id;
		const convKey = vm.$db.buildConversationKey(vm.$enums.CONVERSATION_TYPE.GROUP, m.groupId);
		if (m.sendId && m.sendNickName) {
			const f = vm.friendStore.findFriend(m.sendId);
			if (f && !f.deleted && f.remarkNickName) {
				m.sendNickName = f.remarkNickName;
			}
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.READED) {
			await vm.chatStore.resetUnreadCount(convKey);
			await vm.chatStore.resetAtMessage(convKey);
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.RECEIPT) {
			const message = {
				localId: m.localId,
				readedCount: m.readedCount,
				receiptOk: m.receiptOk,
			};
			await vm.chatStore.updateMessage(convKey, message);
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.RECALL) {
			await vm.chatStore.recallMessage(convKey, m);
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.GROUP_NEW) {
			vm.groupStore.addGroup(JSON.parse(m.content));
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.GROUP_DEL) {
			vm.groupStore.removeGroup(m.groupId);
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.GROUP_TOP_MESSAGE) {
			const topMessage = m.content ? JSON.parse(m.content) : null;
			vm.groupStore.updateTopMessage(m.groupId, topMessage);
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.GROUP_DND) {
			vm.groupStore.setDnd(m.groupId, JSON.parse(m.content));
			await vm.chatStore.setDnd(convKey, JSON.parse(m.content));
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.GROUP_ALL_MUTED) {
			vm.groupStore.setAllMuted(m.groupId, JSON.parse(m.content));
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.GROUP_MEMBER_MUTED) {
			vm.groupStore.setMuted(m.groupId, JSON.parse(m.content));
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.GROUP_TOP) {
			vm.groupStore.setTop(m.groupId, JSON.parse(m.content));
			await vm.chatStore.setTop(convKey, JSON.parse(m.content));
			return;
		}
		if (vm.$msgType.isRtcGroup(m.type)) {
			if (m.type == vm.$enums.MESSAGE_TYPE.RTC_GROUP_INFO) {
				const rtcInfo = JSON.parse(m.content);
				vm.groupStore.setRtcInfo(m.groupId, rtcInfo);
			} else {
				vm.$nextTick(() => {
					this.getRef('rtcGroupVideo')?.onRTCMessage(m);
				});
			}
			return;
		}
		if (vm.$msgType.isNormal(m.type) || vm.$msgType.isTip(m.type) || vm.$msgType.isAction(m.type)) {
			const group = this.loadGroupInfo(vm, m.groupId);
			await this.insertGroupMessage(vm, group, m);
		}
	}

	async insertGroupMessage(vm, group, m) {
		const convKey = vm.$db.buildConversationKey(vm.$enums.CONVERSATION_TYPE.GROUP, group.id);
		const chatInfo = {
			type: vm.$enums.CONVERSATION_TYPE.GROUP,
			targetId: group.id,
			showName: group.showGroupName,
			headImage: group.headImageThumb,
			isDnd: group.isDnd,
			isTop: group.isTop,
		};
		await vm.chatStore.openChat(chatInfo);
		await vm.chatStore.insertMessage(convKey, m);
		if (vm.chatStore.isActive(convKey)) {
			vm.$eventBus.$emit('newMessage', m);
		}
		if (!group.isDnd && !m.selfSend && !vm.chatStore.loading && vm.$msgType.isNormal(m.type)
			&& m.status != vm.$enums.MESSAGE_STATUS.READED) {
			this.playAudioTip(vm);
			const conv = vm.chatStore.conversationMap.get(convKey);
			this.showNotification(vm, conv, group.showGroupName, group.headImageThumb);
		}
	}

	async handleSystemMessage(vm, m) {
		const convKey = vm.$db.buildConversationKey(vm.$enums.CONVERSATION_TYPE.SYSTEM, 0);
		if (m.type == vm.$enums.MESSAGE_TYPE.READED) {
			vm.chatStore.resetUnreadCount(convKey);
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.USER_BANNED) {
			vm.$wsApi.close(3000);
			vm.$alert(vm.$t('home.accountBannedReason', { reason: m.content }), vm.$t('home.accountBannedTitle'), {
				confirmButtonText: vm.$t('common.ok'),
				callback: () => this.onExit(vm),
			});
			return;
		}
		if (m.type == vm.$enums.MESSAGE_TYPE.USER_UNREG) {
			vm.$wsApi.close(3000);
			vm.$alert(vm.$t('home.accountCancelled'), {
				confirmButtonText: vm.$t('common.ok'),
				callback: () => this.onExit(vm),
			});
			return;
		}
		await this.insertSystemMessage(vm, m);
	}

	async insertSystemMessage(vm, m) {
		m.localId = vm.$nextSnowflakeId();
		const convKey = vm.$db.buildConversationKey(vm.$enums.CONVERSATION_TYPE.SYSTEM, 0);
		const headImage = require('@/assets/image/chat_system.png');
		const chatInfo = {
			key: convKey,
			type: vm.$enums.CONVERSATION_TYPE.SYSTEM,
			targetId: 0,
			showName: vm.$t('home.systemNotify'),
			headImage: headImage,
		};
		await vm.chatStore.openChat(chatInfo);
		await vm.chatStore.insertMessage(convKey, m);
	}

	onExit(vm) {
		this.unloadStore(vm);
		vm.$wsApi.close(3000);
		clearLoginSession(true);
		vm.$elm.setTitleTip('');
		vm.$db.close();
		this.initialized = false;
		this.reconnecting = false;
		this.privateMessagesBuffer = [];
		this.groupMessagesBuffer = [];
		this.systemMessagesBuffer = [];
		vm.$router.push('/login');
	}

	playAudioTip(vm) {
		const mine = vm.userStore.userInfo;
		const interval = Date.now() - this.lastPlayAudioTime;
		if (mine.isAudioTip && interval > 1000) {
			if (!this.audio) {
				this.audio = new Audio();
				this.audio.src = require('@/assets/audio/tip.mp3');
			}
			this.lastPlayAudioTime = Date.now();
			this.audio.play();
		}
	}

	showNotification(vm, conv, title, icon) {
		if (this.shouldSuppressNotification()) {
			return;
		}
		if (window.Notification && window.electronAPI) {
			window.electronAPI.invoke('isFocused').then((foucus) => {
				if (!foucus) {
					this.notification && this.notification.close();
					const iconPath = icon ? icon : 'logo.ico';
					this.notification = new Notification(title, {
						body: conv.lastContent,
						icon: iconPath,
					});
					this.notification.onclick = () => {
						vm.chatStore.openChat(conv);
						vm.chatStore.setActive(conv.key);
						vm.$router.push('/home/chat');
						window.electronAPI.sendEvent('show');
					};
				}
			});
		} else {
			vm.$eventBus.$emit('showGlobalNotification', {
				title: title,
				message: conv.lastContent,
				icon: icon,
				chat: conv,
			});
		}
	}

	loadFriendInfo(vm, id) {
		let friend = vm.friendStore.findFriend(id);
		if (!friend) {
			friend = {
				id: id,
				showNickName: vm.$t('home.unknownUser'),
				headImage: '',
			};
		}
		return friend;
	}

	loadGroupInfo(vm, id) {
		let group = vm.groupStore.findGroup(id);
		if (!group) {
			group = {
				id: id,
				showGroupName: vm.$t('home.unknownGroup'),
				headImageThumb: '',
			};
		}
		return group;
	}
}

const imEngine = new ImEngine();

export default imEngine;
